import React, { useState, useRef, useCallback } from 'react';
import { Mic, Square, Upload, RotateCcw } from 'lucide-react';
import { Button } from '../../forms/Button';

interface AudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob, fileName: string) => void;
  maxDurationSeconds?: number;
  minDurationSeconds?: number;
}

export function AudioRecorder({
  onRecordingComplete,
  maxDurationSeconds = 120,
  minDurationSeconds = 30,
}: AudioRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= maxDurationSeconds - 1) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err: unknown) {
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setMicError('Microphone access was denied. Please enable it in your browser settings and try again.');
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        setMicError('No microphone found. Please connect a microphone and try again.');
      } else {
        setMicError('Microphone is unavailable. Please check your browser permissions and try again.');
      }
    }
  }, [maxDurationSeconds, stopRecording]);

  const resetRecording = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
  }, [audioUrl]);

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('audio/')) return;
      setMicError(null);
      setAudioBlob(file);
      setAudioUrl(URL.createObjectURL(file));
    },
    []
  );

  const handleConfirm = useCallback(() => {
    if (audioBlob) {
      const ext = audioBlob.type.includes('webm') ? 'webm' : 'mp3';
      onRecordingComplete(audioBlob, `voice-sample-${Date.now()}.${ext}`);
    }
  }, [audioBlob, onRecordingComplete]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {micError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {micError}
        </p>
      )}
      {!audioBlob ? (
        <div className="space-y-4">
          {/* Recording controls */}
          <div className="flex items-center gap-4">
            {!isRecording ? (
              <Button variant="primary" onClick={() => { setMicError(null); startRecording(); }}>
                <Mic className="w-4 h-4 mr-2" />
                Start Recording
              </Button>
            ) : (
              <Button variant="secondary" onClick={stopRecording}>
                <Square className="w-4 h-4 mr-2 fill-current" />
                Stop ({formatTime(recordingTime)})
              </Button>
            )}
          </div>

          {/* Recording timer bar */}
          {isRecording && (
            <div className="space-y-1">
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    recordingTime >= minDurationSeconds ? 'bg-green-500' : 'bg-yellow-500'
                  }`}
                  style={{ width: `${(recordingTime / maxDurationSeconds) * 100}%` }}
                />
              </div>
              <p className="text-xs text-slate-500">
                {recordingTime < minDurationSeconds
                  ? `Keep recording — ${minDurationSeconds - recordingTime}s more for best quality`
                  : 'Good length! You can stop anytime.'}
              </p>
            </div>
          )}

          {/* File upload alternative */}
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>or</span>
            <button
              type="button"
              className="inline-flex items-center text-blue-600 hover:text-blue-800"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-3 h-3 mr-1" />
              upload an audio file
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Playback */}
          {audioUrl && (
            <audio controls src={audioUrl} className="w-full" />
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="secondary" onClick={resetRecording}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Re-record
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              Use This Recording
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
